import React, { useState, useEffect } from 'react';
import {
    Input,
    Button,
    Spinner,
    SharedSelection,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
} from '@heroui/react';
import {
    UserIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    ArrowRightIcon,
    LanguageIcon,
} from '@heroicons/react/24/outline';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginWithNormalCredentials } from '../../services/api';
import { LoginDto, LoginFormData } from '../../types/types';
import { toast } from 'sonner';

const authInputClassNames = {
    input: 'bg-white',
    inputWrapper: 'bg-white border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]',
    label:
        '!text-[#4B5563] data-[invalid=true]:!text-[#4B5563] group-data-[invalid=true]:!text-[#4B5563] text-left self-start',
    errorMessage: 'text-left ml-1 w-full',
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <div className="text-red-500 text-center">Login failed. Please try again.</div>;
        }
        return this.props.children;
    }
}

const Login: React.FC = () => {
    const { t, i18n } = useTranslation('login');
    const [formData, setFormData] = useState<LoginFormData>({
        email: '',
        password: '',
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [navigate, setNavigate] = useState<boolean>(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(localStorage.getItem('i18nextLng') || 'en');

    const languageNames: { [key: string]: string } = {
        en: t('language_en'),
        de: t('language_de'),
        hr: t('language_hr'),
    };


    const handleLanguageChange = (keys: SharedSelection) => {
        let value: string;
        if (typeof keys === 'string') {
            value = keys;
        } else if ('currentKey' in keys && typeof keys.currentKey === 'string') {
            value = keys.currentKey;
        } else {
            value = 'en';
        }
        setSelectedLanguage(value);
        i18n.changeLanguage(value);
        localStorage.setItem('i18nextLng', value);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const oauthError = urlParams.get('oauth_error');
        if (oauthError) {
            toast.error(t('error_auth_failed'));
        }
    }, [t]);

    const togglePasswordVisibility = (): void => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    const handleEmailChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, email: value }));
        setErrors((prev) => ({ ...prev, email: '' }));
    };

    const handlePasswordChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, password: value }));
        setErrors((prev) => ({ ...prev, password: '' }));
    };

    const validateEmail = (value: string): string | null => {
        if (!value) {
            return t('email_required');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return t('email_invalid');
        }
        return null;
    };

    const validatePassword = (value: string): string | null => {
        if (!value) {
            return t('password_required');
        }
        if (value.length < 8) {
            return t('password_min_length');
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        const emailError = validateEmail(formData.email);
        const passwordError = validatePassword(formData.password);

        if (emailError || passwordError) {
            setErrors({
                email: emailError || '',
                password: passwordError || '',
            });
            return;
        }

        setLoading(true);

        try {
            const loginData: LoginDto = {
                username: formData.email,
                password: formData.password,
            };

            const response = await loginWithNormalCredentials(loginData);

            if (response) {

                localStorage.setItem('loginResponse', JSON.stringify(response));
                localStorage.setItem('i18nextLng', JSON.stringify(response.preferredLanguage));

                toast.success(t('login_success') || 'Login successful!');

                setTimeout(() => {
                    setLoading(false);
                    setNavigate(true);
                }, 1000);
            }

        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                t('error_login_failed');

            toast.error(errorMessage);
            setLoading(false);
        }
    };

    if (navigate) {
        return <Navigate to="/dashboard" state={formData} replace />;
    }

    return (
        <ErrorBoundary>
            <div className="min-h-screen bg-gray-50 flex">
                <div className="hidden lg:block w-1/2 bg-gray-100 p-10 flex flex-col justify-center animate-fade-in">
                    <div className="max-w-md mx-auto">
                        <h1 className="text-4xl font-bold text-gray-800 mb-6 animate-slide-in-left">
                            {t('welcome_title')}
                        </h1>
                        <p className="text-gray-600 text-sm leading-relaxed animate-slide-in-left animation-delay-200">
                            {t('welcome_description')}
                        </p>
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex items-center justify-center p-2 animate-fade-in animation-delay-300 relative">

                    <div className="absolute top-5 right-6 animate-slide-in-up animation-delay-400">
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    variant="flat"
                                    size="sm"
                                    className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50"
                                    aria-label={t('language_select_label')}
                                >
                                    <LanguageIcon className="w-4 h-4 text-gray-600" />
                                    {languageNames[selectedLanguage]}
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                aria-label="Language selection"
                                onSelectionChange={handleLanguageChange}
                                selectionMode="single"
                                selectedKeys={[selectedLanguage]}
                            >
                                <DropdownItem key="en">{t('language_en')}</DropdownItem>
                                <DropdownItem key="de">{t('language_de')}</DropdownItem>
                                <DropdownItem key="hr">{t('language_hr')}</DropdownItem>
                            </DropdownMenu>

                        </Dropdown>
                    </div>

                    <div className="w-full max-w-md">
                        <div className="space-y-4">
                            <div className="text-center animate-slide-in-right">
                                <h2 className="text-2xl font-semibold text-gray-800">{t('sign_in_title')}</h2>
                                <p className="text-gray-600 text-sm mt-1">{t('sign_in_subtitle')}</p>
                            </div>

                            <form
                                noValidate
                                className="w-full space-y-4 animate-slide-in-up animation-delay-400"
                                onSubmit={handleSubmit}
                            >
                                <div className="w-96 max-w-lg mx-auto space-y-10">

                                    <div className="animate-slide-in-up animation-delay-500">
                                        <Input
                                            type="email"
                                            name="email"
                                            label={t('email_label')}
                                            labelPlacement="outside"
                                            placeholder={t('email_placeholder')}
                                            value={formData.email}
                                            onValueChange={handleEmailChange}
                                            className="w-full"
                                            classNames={authInputClassNames}
                                            startContent={
                                                <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                                            }
                                            size="md"
                                            isRequired
                                            validate={validateEmail}
                                            isInvalid={!!errors.email}
                                            errorMessage={errors.email}
                                        />
                                    </div>

                                    <div className="animate-slide-in-up animation-delay-600">
                                        <Input
                                            type={isPasswordVisible ? 'text' : 'password'}
                                            name="password"
                                            label={t('password_label')}
                                            labelPlacement="outside"
                                            placeholder={t('password_placeholder')}
                                            value={formData.password}
                                            onValueChange={handlePasswordChange}
                                            className="w-full"
                                            classNames={authInputClassNames}
                                            startContent={
                                                <LockClosedIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                                            }
                                            endContent={
                                                <button
                                                    className="focus:outline-none"
                                                    type="button"
                                                    onClick={togglePasswordVisibility}
                                                    aria-label="toggle password visibility"
                                                >
                                                    {isPasswordVisible ? (
                                                        <EyeSlashIcon className="w-4 h-4 text-gray-400 pointer-events-none" />
                                                    ) : (
                                                        <EyeIcon className="w-4 h-4 text-gray-400 pointer-events-none" />
                                                    )}
                                                </button>
                                            }
                                            size="md"
                                            isRequired
                                            validate={validatePassword}
                                            isInvalid={!!errors.password}
                                            errorMessage={errors.password}
                                        />
                                    </div>
                                    <div className="animate-slide-in-up animation-delay-800">
                                        <Button
                                            type="submit"
                                            isLoading={loading}
                                            disabled={loading}
                                            color="primary"
                                            size="md"
                                            endContent={loading ? null : <ArrowRightIcon className="w-4 h-4" />}
                                            spinner={<Spinner size="sm" color="white" />}
                                            className="w-full bg-[#1E3A8A] hover:bg-[#2B4A9B] transition-all duration-300 hover:scale-[1.02]"
                                        >
                                            {loading ? '' : t('sign_in_button')}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in { animation: fadeIn 0.6s ease-out forwards; }
          .animate-slide-in-left { animation: slideInLeft 0.8s ease-out forwards; }
          .animate-slide-in-right { animation: slideInRight 0.8s ease-out forwards; }
          .animate-slide-in-up { animation: slideInUp 0.6s ease-out forwards; }
          .animation-delay-200 { animation-delay: 0.2s; opacity: 0; }
          .animation-delay-300 { animation-delay: 0.3s; opacity: 0; }
          .animation-delay-400 { animation-delay: 0.4s; opacity: 0; }
          .animation-delay-500 { animation-delay: 0.5s; opacity: 0; }
          .animation-delay-600 { animation-delay: 0.6s; opacity: 0; }
          .animation-delay-800 { animation-delay: 0.8s; opacity: 0; }
          .animation-delay-900 { animation-delay: 0.9s; opacity: 0; }
          .animation-delay-1000 { animation-delay: 1.0s; opacity: 0; }
          .animation-delay-1100 { animation-delay: 1.1s; opacity: 0; }
        `}</style>
            </div>
        </ErrorBoundary>
    );
};

export default Login;
